import React, { useState, useEffect, useContext } from 'react';
import { getAllLanguages } from '../api/languageApi';
import { getBooksByLanguage } from '../api/bibleApi';
import { AppContext } from '../../context/appContext';

/**
 * BibleChapterPicker - Select Language → Book → Chapter
 * @param {function} onSelect - Callback when selection is complete (languageId, bookCode, chapter, metadata)
 * @param {object} initialValue - Initial selection { languageId, bookCode, chapter }
 */
const BibleChapterPicker = ({ onSelect, initialValue = null }) => {
  const { t, isRTL } = useContext(AppContext);

  const [languages, setLanguages] = useState([]);
  const [books, setBooks] = useState([]);
  const [selectedLanguageId, setSelectedLanguageId] = useState(initialValue?.languageId || '');
  const [selectedBookCode, setSelectedBookCode] = useState(initialValue?.bookCode || '');
  const [selectedChapter, setSelectedChapter] = useState(initialValue?.chapter || '');
  const [maxChapters, setMaxChapters] = useState(150);
  const [hasIntroduction, setHasIntroduction] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch languages on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await getAllLanguages(1, 100, '');
        if (response?.data?.data && Array.isArray(response.data.data)) {
          setLanguages(response.data.data);
        } else if (response?.data && Array.isArray(response.data)) {
          setLanguages(response.data);
        } else {
          setLanguages([]);
        }
      } catch (error) {
        console.error('Error fetching languages:', error);
        setLanguages([]);
      }
    };
    fetchLanguages();
  }, []);

  // Fetch books when language changes
  useEffect(() => {
    if (selectedLanguageId) {
      const fetchBooks = async () => {
        setLoading(true);
        try {
          const response = await getBooksByLanguage(selectedLanguageId);
          if (response?.data?.data && Array.isArray(response.data.data)) {
            setBooks(response.data.data);
          } else if (response?.data && Array.isArray(response.data)) {
            setBooks(response.data);
          } else {
            setBooks([]);
          }
        } catch (error) {
          console.error('Error fetching books:', error);
          setBooks([]);
        } finally {
          setLoading(false);
        }
      };
      fetchBooks();
    } else {
      setBooks([]);
      setSelectedBookCode('');
      setSelectedChapter('');
    }
  }, [selectedLanguageId]);

  // Update max chapters when book changes
  useEffect(() => {
    if (selectedBookCode) {
      const book = books.find(b => b.bookCode === selectedBookCode);
      if (book) {
        setMaxChapters(book.chapterCount || 150);
        setHasIntroduction(book.hasIntroduction || false);

        // If all selections are made, call onSelect
        if (selectedLanguageId && selectedBookCode && selectedChapter) {
          const language = languages.find(l => l._id === selectedLanguageId);
          const chapterDisplay = selectedChapter === '0'
            ? (isRTL ? 'مقدمه' : 'Introduction')
            : `${t('bible.chapter') || 'Chapter'} ${selectedChapter}`;

          onSelect({
            languageId: selectedLanguageId,
            bookCode: selectedBookCode,
            chapter: selectedChapter,
            metadata: {
              languageName: language?.name || '',
              bookTitle: book.title || book.bookCode,
              bibleDescription: `${book.title || book.bookCode} - ${chapterDisplay} (${language?.name || ''})`
            }
          });
        }
      }
    } else {
      setSelectedChapter('');
      setHasIntroduction(false);
    }
  }, [selectedBookCode, books]);

  // Trigger onSelect when chapter changes
  useEffect(() => {
    if (selectedLanguageId && selectedBookCode && selectedChapter) {
      const language = languages.find(l => l._id === selectedLanguageId);
      const book = books.find(b => b.bookCode === selectedBookCode);

      if (language && book) {
        const chapterDisplay = selectedChapter === '0'
          ? (isRTL ? 'مقدمه' : 'Introduction')
          : `${t('bible.chapter') || 'Chapter'} ${selectedChapter}`;

        onSelect({
          languageId: selectedLanguageId,
          bookCode: selectedBookCode,
          chapter: selectedChapter,
          metadata: {
            languageName: language.name || '',
            bookTitle: book.title || book.bookCode,
            bibleDescription: `${book.title || book.bookCode} - ${chapterDisplay} (${language.name || ''})`
          }
        });
      }
    }
  }, [selectedChapter]);

  return (
    <div className="space-y-4">
      {/* Language Selector */}
      <div>
        <label className="label">
          <span className="label-text">{t('bible.selectLanguage') || 'Select Language'}</span>
        </label>
        <select
          className="select select-bordered w-full"
          value={selectedLanguageId}
          onChange={(e) => {
            setSelectedLanguageId(e.target.value);
            setSelectedBookCode('');
            setSelectedChapter('');
          }}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <option value="">{t('bible.chooseLanguage') || '-- Choose Language --'}</option>
          {languages.map(lang => (
            <option key={lang._id} value={lang._id}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>

      {/* Book Selector */}
      <div>
        <label className="label">
          <span className="label-text">{t('bible.selectBook') || 'Select Book'}</span>
        </label>
        <select
          className="select select-bordered w-full"
          value={selectedBookCode}
          onChange={(e) => {
            setSelectedBookCode(e.target.value);
            setSelectedChapter('');
          }}
          disabled={!selectedLanguageId || loading}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <option value="">{t('bible.chooseBook') || '-- Choose Book --'}</option>
          {books.map(book => (
            <option key={book._id} value={book.bookCode}>
              {book.title || book.bookCode}
            </option>
          ))}
        </select>
      </div>

      {/* Chapter Selector */}
      <div>
        <label className="label">
          <span className="label-text">{t('bible.selectChapter') || 'Select Chapter'}</span>
        </label>
        <select
          className="select select-bordered w-full"
          value={selectedChapter}
          onChange={(e) => setSelectedChapter(e.target.value)}
          disabled={!selectedBookCode}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <option value="">{t('bible.chooseChapter') || '-- Choose Chapter --'}</option>
          {hasIntroduction && (
            <option key="0" value="0">
              {t('bible.introduction') || 'مقدمه'}
            </option>
          )}
          {Array.from({ length: maxChapters }, (_, i) => i + 1).map(num => (
            <option key={num} value={num}>
              {t('bible.chapter') || 'Chapter'} {num}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default BibleChapterPicker;
